<template>
  <LoadingLayout :isLoading='state.loading' class='h-screen'>
    <RouterView v-slot="{ Component, route }">
      <template v-if='route.meta.keepAlive'>
        <KeepAlive>
          <Component :is="Component" :key="$route.fullPath" />
        </KeepAlive>
      </template>
      <template v-else>
        <Component :is="Component" />
      </template>
    </RouterView>
    <ToastManager />
    <SessionModal />
  </LoadingLayout>
  <ConfirmAlert :controller='sessionAlertController' :showCancel='false'>
    <template #title>
      Session Expired
    </template>
    <template #message>
      Your session has expired, please login again.
    </template>
    <template #confirm>
      Login
    </template>
  </ConfirmAlert>
  <ConfirmAlert :controller='versionAlertController'>
    <template #title>
      Application Outdated
    </template>
    <template #message>
      Your session has expired, please login again as soon as possible.
    </template>
    <template #cancel>
      Reload Later
    </template>
    <template #confirm>
      Reload Now
    </template>
  </ConfirmAlert>
  <ErrorAlert :controller='errorAlertController' />
</template>

<script setup>

import { reactive, onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';

import { checkSession, isAuthenticated, hasAttemptedHydration, getOrganisationId } from '@/kernel/session';
import { debounce } from '@/kernel/debounce';
import { useAlertController } from '@/kernel/controllers/alert-controller';

import LoadingLayout from '@/components/layouts/loading-layout.vue';
import ToastManager from '@/components/managers/toast-manager.vue';
import SessionModal from '@/components/modals/session-modal.vue';
import ConfirmAlert from '@/components/alerts/confirm-alert.vue';
import ErrorAlert from '@/components/alerts/error-alert.vue';

const sessionAlertController = useAlertController();
const versionAlertController = useAlertController();
const errorAlertController = useAlertController();

const state = reactive({
  loading: true
});

window.addEventListener('warn_application_version_outdated', async () => {
  if (versionAlertController.isPresenting()) {
    return;
  }

  const { result } = await versionAlertController.present();

  if (result !== 'confirm') {
    return;
  }

  window.location.reload();
});

window.addEventListener('error', (e) => {
  errorAlertController.present({
    data: {error: e.error}
  });

  return false;
});

window.addEventListener('unhandledrejection', (e) => {
  errorAlertController.present({
    data: {error: e.reason}
  });

  return false;
});

window.addEventListener('focus', async () => {
  rehydrate();
});

const rehydrate = debounce(async () => {
  if (!isAuthenticated()) {
    return;
  }

  try {
    const user = await checkSession();

    if (!user) {
      const { result } = await sessionAlertController.present();

      if (result === 'confirm') {
        window.location.reload();
      }
    }
  } catch(e) {
    errorAlertController.present({
      data: {error: e}
    });
  }
}, 100);

watch(() => hasAttemptedHydration(), () => {
  state.loading = false;
});

</script>
